import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import {
  isLoggedInUser,
  promptLoginPage,
} from '../../../contexts/User/helpers/userContextHelpers';
import { getParticipantOrderId } from '../../../selectors/participants';
import { renderSeoMetatagsPage } from '../../../services/SeoMetatagsPage';
import { needToRedirectToLogin } from './needToRedirectToLogin';
import {
  addOnLoginHandler,
  IOnLoginHandlerPriority,
} from '../../../contexts/GeneralDataProvider/helpers/onLogin';

import { getInitialProps } from './getInitialProps';
import { goToVisitorPage } from './goToVisitorPage';
import { createOrderOrSendJoinRequest } from './createOrderOrSendJoinRequest';
import { isRedirectingToVisitorOrParticipantPage } from './isRedirectingToVisitorOrParticipantPage';

export const createParticipantFlow = async ({
  flowAPI,
}: {
  flowAPI: ControllerFlowAPI;
}) => {
  addOnLoginHandler({
    priority: IOnLoginHandlerPriority.SECONDARY,
    handler: async (flowAPI: ControllerFlowAPI) => {
      // we just logged in, so we need to check for redirects before proceeding with order
      const initialProps = await getInitialProps(flowAPI);
      if (
        await isRedirectingToVisitorOrParticipantPage({ initialProps, flowAPI })
      ) {
        return;
      }

      createOrderOrSendJoinRequest({
        initialProps,
        flowAPI,
      });
    },
  });

  const initialProps = await getInitialProps(flowAPI);

  // not logged in, private program
  // go login to see if already purchased
  if (needToRedirectToLogin({ initialProps, flowAPI })) {
    flowAPI.controllerConfig.setProps({
      isForcedBlankPage: true,
    });

    try {
      await promptLoginPage(flowAPI);
    } catch {
      // user closed the login popup - redirect to visitor page
      goToVisitorPage({
        challengeData: initialProps.challengeData,
        flowAPI,
      });
    }
    return;
  }

  // logged in, public / private & approved, already joined
  // private program, waiting for owners approval
  if (
    await isRedirectingToVisitorOrParticipantPage({ initialProps, flowAPI })
  ) {
    return;
  }

  // logged in, public / private & approved, not purchased yet
  if (
    isLoggedInUser(flowAPI) &&
    !getParticipantOrderId(initialProps?.participant) &&
    flowAPI.environment.isViewer
  ) {
    createOrderOrSendJoinRequest({
      initialProps,
      flowAPI,
    });
    return;
  }

  flowAPI.controllerConfig.setProps({ ...initialProps });
  renderSeoMetatagsPage(
    flowAPI,
    initialProps?.challengeData?.challenge,
    'PAYMENT_PAGE',
  );
};
